import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

const fmtCurrency = n => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);

const EMPTY = { category_id: '', name: '', description: '', duration_minutes: 30, tolerance_minutes: 10, price: '' };

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | service object
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [catModal, setCatModal] = useState(false);
  const [catName, setCatName] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/services'), api.get('/services/categories')])
      .then(([s, c]) => { setServices(s.data); setCategories(c.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setForm(EMPTY); setModal('create'); setError(''); };
  const openEdit = svc => { setForm({ ...svc, price: svc.price }); setModal(svc); setError(''); };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (modal === 'create') {
        await api.post('/services', form);
      } else {
        await api.put(`/services/${modal.id}`, form);
      }
      setModal(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const toggle = async id => {
    await api.patch(`/services/${id}/toggle`);
    load();
  };

  const createCategory = async () => {
    if (!catName.trim()) return;
    await api.post('/services/categories', { name: catName });
    setCatName('');
    setCatModal(false);
    load();
  };

  const grouped = services.reduce((acc, s) => {
    const cat = s.category_name || 'Sin categoría';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <h2>Servicios</h2>
          <p>Gestiona los servicios ofrecidos en tu barbería</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => setCatModal(true)}>+ Categoría</button>
          <button className="btn btn-primary" onClick={openCreate}>+ Nuevo Servicio</button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : services.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">💈</div>
          <h3>Sin servicios</h3>
          <p>Agrega tu primer servicio para comenzar a tomar citas</p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={openCreate}>+ Crear servicio</button>
        </div>
      ) : (
        Object.entries(grouped).map(([cat, svcs]) => (
          <div key={cat} className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <h3 className="card-title">💈 {cat}</h3>
              <span className="badge badge-gold">{svcs.length}</span>
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Nombre</th><th>Duración</th><th>Tolerancia</th><th>Precio</th><th>Estado</th><th>Acciones</th></tr>
                </thead>
                <tbody>
                  {svcs.map(s => (
                    <tr key={s.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{s.name}</div>
                        {s.description && <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{s.description}</div>}
                      </td>
                      <td>{s.duration_minutes} min</td>
                      <td>{s.tolerance_minutes} min</td>
                      <td style={{ color: 'var(--color-gold)', fontWeight: 600 }}>{fmtCurrency(s.price)}</td>
                      <td><span className={`badge badge-${s.is_active ? 'active' : 'inactive'}`}>{s.is_active ? 'Activo' : 'Inactivo'}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-sm btn-secondary" onClick={() => openEdit(s)}>✏️ Editar</button>
                          <button className={`btn btn-sm ${s.is_active ? 'btn-danger' : 'btn-secondary'}`} onClick={() => toggle(s.id)}>
                            {s.is_active ? 'Desactivar' : 'Activar'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {/* Category Modal */}
      {catModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <span className="modal-title">Nueva Categoría</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setCatModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Nombre de la categoría</label>
                <input className="form-input" value={catName} onChange={e => setCatName(e.target.value)} placeholder="Ej: Cortes, Barba..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCatModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={createCategory}>Crear</button>
            </div>
          </div>
        </div>
      )}

      {/* Service Form Modal */}
      {modal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <span className="modal-title">{modal === 'create' ? 'Nuevo Servicio' : 'Editar Servicio'}</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-error">{error}</div>}
                <div className="form-grid">
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Nombre del servicio *</label>
                    <input className="form-input" required value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Corte Tradicional" />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Descripción</label>
                    <textarea className="form-textarea" value={form.description || ''}
                      onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Categoría</label>
                    <select className="form-select" value={form.category_id || ''}
                      onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}>
                      <option value="">Sin categoría</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Precio (COP) *</label>
                    <input type="number" className="form-input" required min={0} value={form.price}
                      onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Duración (min) *</label>
                    <input type="number" className="form-input" required min={5} value={form.duration_minutes}
                      onChange={e => setForm(f => ({ ...f, duration_minutes: Number(e.target.value) }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tolerancia (min) *</label>
                    <input type="number" className="form-input" required min={0} value={form.tolerance_minutes}
                      onChange={e => setForm(f => ({ ...f, tolerance_minutes: Number(e.target.value) }))} />
                    <span className="form-hint">Tiempo extra entre citas</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><div className="spinner" />Guardando...</> : '💾 Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
